import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import {
  cellState,
  electroMessages,
  type NernstStrings,
} from "@/components/tools/chem-physical/nernst-panel";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  FREE_ENERGY_UNITS,
  type FreeEnergySolution,
  type FreeEnergyTerm,
  solveFreeEnergy,
  VOLTAGE_UNITS,
} from "@/lib/tool-kit/chem-physical/electro";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const DEFAULT_SIG = 3;
const TERMS: FreeEnergyTerm[] = ["g", "n", "e"];
const ENERGY_OPTIONS = FREE_ENERGY_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const VOLT_OPTIONS = VOLTAGE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));

function FreeEnergyAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: FreeEnergySolution;
  texts: { g: string; n: string; e: string };
  units: { g: string; e: string };
  sig: number;
  strings: NernstStrings;
}) {
  const fmt = useToolNumber();
  const { unknown } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const labels: Record<FreeEnergyTerm, string> = {
    g: strings.termDG,
    n: strings.termN,
    e: strings.termE,
  };
  const unitSymbol = (
    list: readonly { id: string; symbol: string }[],
    id: string
  ) => list.find((u) => u.id === id)?.symbol ?? id;
  const answerText = {
    g: `${formatSig(fmt, solution.answer, sig)} ${unitSymbol(FREE_ENERGY_UNITS, units.g)}`,
    e: `${formatSig(fmt, solution.answer, sig)} ${unitSymbol(VOLTAGE_UNITS, units.e)}`,
    n: formatSig(fmt, solution.answer, sig),
  }[unknown];
  const state = {
    dead: strings.cellDead,
    galvanic: strings.galvanic,
    electrolytic: strings.nonSpontaneousCell,
  }[cellState(solution.e)];
  const conversions: string[] = [];
  if (unknown !== "g" && units.g !== "J") {
    conversions.push(
      t(strings.convertLine, {
        label: labels.g,
        value: texts.g.trim(),
        unit: unitSymbol(FREE_ENERGY_UNITS, units.g),
        converted: `${p(solution.g)} J/mol`,
      })
    );
  }
  if (unknown !== "e" && units.e !== "V") {
    conversions.push(
      t(strings.convertLine, {
        label: labels.e,
        value: texts.e.trim(),
        unit: units.e,
        converted: `${p(solution.e)} V`,
      })
    );
  }
  const result = {
    g: `${formatSig(fmt, solution.g, sig + 1)} J/mol`,
    e: `${formatSig(fmt, solution.e, sig + 1)} V`,
    n: formatSig(fmt, solution.n, sig + 1),
  }[unknown];
  const substituted = t(
    {
      g: strings.feSubstitutedG,
      e: strings.feSubstitutedE,
      n: strings.feSubstitutedN,
    }[unknown],
    { g: p(solution.g), n: p(solution.n), e: p(solution.e), result }
  );
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard label={strings.cellState} value={state} />
      </StatGrid>
      {solution.fractionalN ? (
        <Notice>{t(strings.fractionalN, { n: p(solution.n) })}</Notice>
      ) : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.feEquation}</p>
        {unknown === "g" ? null : (
          <p>
            {unknown === "e" ? strings.feRearrangedE : strings.feRearrangedN}
          </p>
        )}
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        <p>{strings.constantsNote}</p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function FreeEnergyPanel({
  strings,
  sigChoice,
}: {
  strings: NernstStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<FreeEnergyTerm>("g");
  const [text, setText] = useState({ g: "", n: "2", e: "1.10" });
  const [unit, setUnit] = useState({ g: "kJ", e: "V" });
  const outcome = solveFreeEnergy({ unknown, text, unit });
  // The electron count is an exact integer, so it never limits the significant figures.
  const typed = TERMS.filter((term) => term !== unknown && term !== "n").map(
    (term) => text[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<FreeEnergyTerm, string> = {
    g: strings.termDG,
    n: strings.termN,
    e: strings.termE,
  };
  const label = (term: FreeEnergyTerm) =>
    unknown === term
      ? t(strings.answerSuffix, { label: labels[term] })
      : labels[term];
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="fe-solve"
        onChange={setUnknown}
        options={TERMS.map((term) => ({ value: term, label: labels[term] }))}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityInput
          id="fe-g"
          label={label("g")}
          onUnitChange={(value) => setUnit({ ...unit, g: value })}
          onValueChange={(value) => setText({ ...text, g: value })}
          solved={unknown === "g"}
          unit={unit.g}
          unitLabel={strings.unitFreeEnergy}
          units={ENERGY_OPTIONS}
          value={text.g}
        />
        {unknown === "n" ? (
          <div className="space-y-2">
            <p className="font-medium text-foreground text-sm">{label("n")}</p>
            <p className="rounded-md border border-primary/40 border-dashed bg-primary/5 px-3 py-2 text-foreground text-sm">
              {strings.solvedCell}
            </p>
          </div>
        ) : (
          <TextField
            hint={strings.nHint}
            id="fe-n"
            label={label("n")}
            numeric
            onChange={(value) => setText({ ...text, n: value })}
            value={text.n}
          />
        )}
        <QuantityInput
          id="fe-e"
          label={label("e")}
          onUnitChange={(value) => setUnit({ ...unit, e: value })}
          onValueChange={(value) => setText({ ...text, e: value })}
          solved={unknown === "e"}
          unit={unit.e}
          unitLabel={strings.unitVoltage}
          units={VOLT_OPTIONS}
          value={text.e}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <FreeEnergyAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={{ n: strings.termN, g: strings.termDG, e: strings.termE }}
            messages={electroMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
